import { z } from "zod";
import type { LessonCourse } from "./schema.ts";
import { whiteRepertoireMoves } from "./coverage.ts";

export const sanityEntrySchema = z.object({
  after: z.array(z.string()),
  san: z.string().min(2),
  band: z.enum(["top", "equivalent", "playable", "inferior", "unknown"]),
  override: z.enum(["reviewed-book", "reviewed-gambit", "pending"]).optional(),
  note: z.string().min(1),
});

export const sanityReportSchema = z.object({
  courseId: z.string(),
  engine: z.string(),
  depth: z.number().int().min(0),
  generatedAt: z.string(),
  entries: z.array(sanityEntrySchema),
});

export type SanityReport = z.infer<typeof sanityReportSchema>;

export type SanityIssue = { code: string; message: string };

function keyFor(after: string[], san: string): string {
  return `${after.join(" ")}|${san}`;
}

export function validateSanityReport(course: LessonCourse, report: SanityReport): SanityIssue[] {
  const issues: SanityIssue[] = [];
  const parsed = sanityReportSchema.safeParse(report);
  if (!parsed.success) {
    for (const issue of parsed.error.issues) {
      issues.push({ code: "schema", message: `${issue.path.join(".")}: ${issue.message}` });
    }
    return issues;
  }
  if (report.courseId !== course.id) {
    issues.push({
      code: "course",
      message: `report is for ${report.courseId}, course is ${course.id}`,
    });
  }
  const byKey = new Map(report.entries.map((entry) => [keyFor(entry.after, entry.san), entry]));
  for (const white of whiteRepertoireMoves(course)) {
    const entry = byKey.get(keyFor(white.after, white.san));
    if (!entry) {
      issues.push({
        code: "missing",
        message: `no sanity entry for ${white.after.join(" ") || "(start)"} ${white.san}`,
      });
      continue;
    }
    if (entry.band === "inferior" && entry.override !== "reviewed-gambit") {
      issues.push({
        code: "override",
        message: `inferior ${white.san} after ${white.after.join(" ")} needs reviewed-gambit override`,
      });
    }
  }
  return issues;
}
